/**
 * Hook to log activity on mount (page visit) and provide logActivity for manual actions
 */
import { useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { logActivity } from "../services/activityService";

const MODULE_MAP = {
  "/": "DASHBOARD",
  "/profile": "PROFILE",
  "/profile/profile-details": "PROFILE",
  "/profile/add-skills": "PROFILE",
  "/profile/edit-skills": "PROFILE",
  "/profile/edit-skill": "PROFILE",
  "/learnings": "MY_LEARNINGS",
  "/new/learnings": "MODULES",
  "/new/learnings/detail": "LEARNING_DETAIL",
  "/syllabus": "SYLLABUS",
  "/lesson": "LESSON",
  "/lerning/detail": "LEARNING_DETAIL",
  "/singletopic": "TOPIC_SINGLE",
  "/skills": "CATEGORY",
  "/support": "SUPPORT",
  "/community": "COMMUNITY",
  "/requests": "REQUESTS",
  "/message": "MESSAGE",
  "/answers": "ANSWERS",
  "/calendar": "CALENDAR",
  "/usercalendar": "CALENDAR",
  "/transactionhistory": "TRANSACTION_HISTORY",
  "/peerprofile": "CONNECTION_PROFILE",
  "/billing": "BILLING",
  "/exams": "EXAMS",
  "/exam/summary": "EXAM_SUMMARY",
  "/quiz": "QUIZ",
  "/quiz/questions": "QUIZ_QUESTIONS",
  "/forum": "FORUM",
  "/events": "EVENTS",
  "/event/details": "EVENT_DETAILS",
  "/assessments": "ASSESSMENTS",
  "/daily/drill": "DAILY_DRILL",
  "/weekly/workout": "WEEKLY_WORKOUT",
  "/subscribe/details": "SUBSCRIPTION",
  "/performance": "PERFORMANCE",
  "/activity": "ACTIVITY",
  "/task/completion": "TASK_COMPLETION",
  "/insights": "INSIGHTS",
  "/opportunities": "OPPORTUNITIES",
  "/offers": "OFFERS",
  "/job-view": "JOB_DETAILS",
  "/chat": "CHAT",
  "/login": "AUTH",
  "/register": "AUTH",
  "/search": "SEARCH",
  "/roleselection": "ROLE_SELECTION",
};

function getModuleFromPath(pathname) {
  for (const [path, module] of Object.entries(MODULE_MAP)) {
    if (pathname === path || (path !== "/" && pathname.startsWith(path))) {
      return module;
    }
  }
  return "UNKNOWN";
}

/**
 * Logs PAGE_VISIT on mount and returns logActivity for manual logging
 * @param {string} [moduleOverride] - Override auto-detected module
 */
export function useActivityLog(moduleOverride) {
  const location = useLocation();

  useEffect(() => {
    const module = moduleOverride || getModuleFromPath(location.pathname);
    logActivity({
      module,
      action: "PAGE_VISIT",
      location: location.pathname,
      remark: `Visited ${location.pathname}`,
    });
  }, [location.pathname, moduleOverride]);

  const logAction = useCallback((action, options = {}) => {
    const module = moduleOverride || getModuleFromPath(location.pathname);
    logActivity({
      module,
      action,
      location: location.pathname,
      ...options,
    });
  }, [location.pathname, moduleOverride]);

  return { logActivity: logAction };
}

export { logActivity };
